// ============================================================
// xFreeScan - Plan & Usage Hook
// Provides plan info, limits, and usage across the app
// ============================================================
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from './useAuth';

const API_URL = import.meta.env.VITE_API_URL || 'https://xfreescan-api.aforero.workers.dev';

interface PlanUsage {
  plan: 'free' | 'pro' | 'enterprise';
  daily: {
    used: number;
    limit: number;
    remaining: number;
  };
  total_scans: number;
  limits: {
    max_targets: number;
    history_days: number;
    report_type: string;
    export_pdf: boolean;
  };
}

interface PlanComparison {
  current_plan: string;
  current_modules: number;
  pro_modules: number;
  additional_modules: Array<{
    name: string;
    displayName: string;
    description: string;
    tier: string;
    isActive: boolean;
  }>;
  additional_count: number;
  upgrade_benefits: string[];
}

export function usePlan() {
  const auth = useAuth();
  const user = auth?.user;
  const token = auth?.token;
  const [usage, setUsage] = useState<PlanUsage | null>(null);
  const [comparison, setComparison] = useState<PlanComparison | null>(null);
  const [loading, setLoading] = useState(false);

  const plan = (user?.plan || 'free') as 'free' | 'pro' | 'enterprise';
  const isFree = plan === 'free';
  const isPro = plan === 'pro' || plan === 'enterprise';

  const fetchUsage = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/billing/usage`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) setUsage(data.data);
    } catch (e) {
      console.error('Error fetching plan usage:', e);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchComparison = useCallback(async () => {
    if (!token || isPro) return;
    try {
      const res = await fetch(`${API_URL}/api/billing/compare`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) setComparison(data.data);
    } catch (e) {
      console.error('Error fetching plan comparison:', e);
    }
  }, [token, isPro]);

  useEffect(() => {
    fetchUsage();
    fetchComparison();
  }, [fetchUsage, fetchComparison]);

  return {
    plan,
    isFree,
    isPro,
    usage,
    comparison,
    loading,
    refresh: fetchUsage,

    // Convenience checks
    canScanToday: usage ? usage.daily.remaining > 0 : true,
    scansRemaining: usage?.daily.remaining ?? 3,
    scansUsedToday: usage?.daily.used ?? 0,
    dailyLimit: usage?.daily.limit ?? 3,
    canExportPdf: isPro,
    reportType: isPro ? 'executive' : 'basic',
  };
}
